import { getEstimateItemsList, getEstimateValidItems, itemListSources } from "@Actions/estimation";
import { HeaderWithIcon } from "@CubeComponents/html/SlickGrid1/Formatters/Formatters";
import i18n from "@i18n";
import {
  estimateItemsForContractUrl,
  estimateItemsUrl,
  getCostDatabaseItemsUrl,
  getValidItemsUrl,
} from "@Services/dataManager";
import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { type Formatter, Formatters } from "slickgrid-react";
import type { GetValidItemsRequest } from "@ServerTypes/estimates/estimates.schema.js";
import type { EstimateItemDocument } from "@ServerTypes/estimates/estimates.collection.js";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

type BOQToolId = (typeof BOQToolsIdsMap)[keyof typeof BOQToolsIdsMap];

type BOQEditorTemplate = () => React.ReactNode;

interface BOQColumn {
  id: string | number;
  field: string;
  name?: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  filterType?: string;
  clipMode?: string;
  wrapText?: boolean;
  initiallyHidden?: boolean;
  hidden?: boolean;
  disableGrouping?: boolean;
  disableFiltering?: boolean;
  disableSorting?: boolean;
  headerRenderer?: () => string | HTMLElement;
  formatter?: Formatter;
  editorTemplate?: BOQEditorTemplate;
  _excelValueGetter?: (row: EstimateItemDocument) => string | number | undefined;
}

interface GetGridToolsProps {
  source: (typeof itemListSources)[keyof typeof itemListSources];
  canEdit: boolean;
  costDatabaseVersionId?: string;
  estimateVersionId?: string;
  toolbarButtonDisableState: Partial<Record<BOQToolId, boolean>>;
  addTopButtonTemplate?: () => {};
  addChildButtonTemplate?: () => {};
  propertiesBtnTemplate?: () => {};
  toggleTemplate?: () => {};
  formLinking?: unknown;
  disableExport: boolean;
}

interface RemoteDataQuery {
  estimateId?: string;
  versionId?: string;
  costDatabaseId?: string;
  publishedVersion?: boolean;
  contractId?: string;
  contractVersionId?: string;
  wbsOriginalItemId?: string;
  parentContractId?: string;
  contractType?: string;
  formId?: string;
  taskId?: string;
  getAllItems?: boolean;
  params?: Record<string, string | boolean | undefined>;
}

interface GetRemoteDataServicesProps {
  source: (typeof itemListSources)[keyof typeof itemListSources];
  estimateVersionId?: string;
  estimateId?: string;
  queryProp?: RemoteDataQuery;
  costDatabaseId?: string;
  costDatabaseVersionId?: string;
  publishedVersion?: boolean;
  wbsOriginalItemId?: string;
  contractVersionId?: string;
  contractId?: string;
  taskLinking?: boolean;
  formLinking?: boolean;
  formId?: string;
  taskId?: string;
  parentContractId?: string;
  contractType?: string;
}

type RemoteDataSourceType = undefined | string;
type GetRemoteDataServicesOutput = [RemoteDataSourceType, RemoteDataQuery | undefined];

type getStaticDataSourceArgs = {
  source: (typeof itemListSources)[keyof typeof itemListSources];
  parentId?: string | null;
  estimateId?: ObjectIdString;
  estimateVersionId?: ObjectIdString | null;
  costDatabaseId?: ObjectIdString;
  costDatabaseVersionId?: ObjectIdString;
  publishedVersion?: boolean;
  getAllItems?: boolean;
  queryProp?: GetValidItemsRequest["value"];
};

type GetEstimateAndCostDatabaseItemsArgs = Parameters<typeof getEstimateItemsList>[0];

interface CurrencyData {
  selectedSymbol: {
    name: string;
  };
}

interface GetGridColumnsProps {
  source: (typeof itemListSources)[keyof typeof itemListSources];
  showHeaderWithIcon: boolean;
  showInfoColumn: boolean;
  costDatabaseCurrencySymbol: string;
  currencyData: CurrencyData;
  detailTemplate: Formatter;
  serialTemplate: Formatter;
  totalQuantityTemplate: Formatter;
  remainingQuantityTemplate: Formatter;
  usedQuantityTemplate: Formatter;
  quantityTemplate: Formatter;
  unitTemplate: Formatter;
  rateTemplate: Formatter;
  amountTemplate: Formatter;
  remainingAmountTemplate: Formatter;
  textEditTemplate: (field: string) => React.ReactNode;
  quantityEditTemplate: BOQEditorTemplate;
  unitEditTemplate: BOQEditorTemplate;
  rateEditTemplate: BOQEditorTemplate;
  linkedAmountTemplate: Formatter;
}

export const BOQToolsIdsMap = {
  ADD_TOP: "add_top",
  ADD_CHILD: "add_child",
  EDIT: "edit_item",
  DELETE: "delete",
  UPDATE: "update",
  CANCEL: "cancel",
  CHANGE_TYPE: "changeType",
  EXPAND_ALL: "expand_all",
  COLLAPSE_ALL: "collapse_all",
  IMPORT_EXCEL: "importExcel",
  MODIFY_TOTAL: "modify_total",
  EXPORT_EXCEL: "export_excel",
  EXPORT_PDF: "export_pdf",
  PROPERTIES: "item_properties",
  TOGGLE: "toggle",
};

export const gridTools1 = ({
  source,
  canEdit = true,
  costDatabaseVersionId,
  estimateVersionId,
  toolbarButtonDisableState,
  addTopButtonTemplate,
  addChildButtonTemplate,
  propertiesBtnTemplate,
  toggleTemplate,
  formLinking,
  disableExport = false,
}: GetGridToolsProps) => {
  const { t } = i18n;
  let disabled = false;
  if (costDatabaseVersionId || estimateVersionId) {
    disabled = true;
  }

  const allTools = [
    {
      id: BOQToolsIdsMap.TOGGLE,
      template: toggleTemplate,
      disabled: toolbarButtonDisableState[BOQToolsIdsMap.TOGGLE],
    },
    {
      id: BOQToolsIdsMap.ADD_TOP,
      template: addTopButtonTemplate,
      disabled: !canEdit || toolbarButtonDisableState[BOQToolsIdsMap.ADD_TOP],
    },
    {
      id: BOQToolsIdsMap.ADD_CHILD,
      template: addChildButtonTemplate,
      disabled: !canEdit || toolbarButtonDisableState[BOQToolsIdsMap.ADD_CHILD],
    },
    {
      id: BOQToolsIdsMap.EDIT,
      name: t("Edit"),
      prefixIcon: "editProfile",
      disabled: !canEdit || toolbarButtonDisableState[BOQToolsIdsMap.EDIT],
    },
    {
      id: BOQToolsIdsMap.DELETE,
      name: t("Delete"),
      prefixIcon: "delete",
      disabled: !canEdit || toolbarButtonDisableState[BOQToolsIdsMap.DELETE],
    },
    {
      id: BOQToolsIdsMap.UPDATE,
      name: t("Update"),
      prefixIcon: "Update",
      disabled: !canEdit || toolbarButtonDisableState[BOQToolsIdsMap.UPDATE],
    },
    {
      id: BOQToolsIdsMap.CANCEL,
      name: t("Cancel"),
      prefixIcon: "Cancel",
      disabled: !canEdit || toolbarButtonDisableState[BOQToolsIdsMap.CANCEL],
    },
    {
      id: BOQToolsIdsMap.CHANGE_TYPE,
      name: t("Change Type"),
      prefixIcon: "changeType",
      disabled: !canEdit || toolbarButtonDisableState[BOQToolsIdsMap.CHANGE_TYPE],
    },
    {
      id: BOQToolsIdsMap.EXPAND_ALL,
      name: t("Expand All"),
      prefixIcon: "Expand",
      disabled: toolbarButtonDisableState[BOQToolsIdsMap.EXPAND_ALL],
    },
    {
      id: BOQToolsIdsMap.COLLAPSE_ALL,
      name: t("Collapse All"),
      prefixIcon: "Collapse",
      disabled: toolbarButtonDisableState[BOQToolsIdsMap.COLLAPSE_ALL],
    },
    {
      id: BOQToolsIdsMap.IMPORT_EXCEL,
      name: t("Import Excel"),
      prefixIcon: "ImportExcel",
      disabled: !canEdit || toolbarButtonDisableState[BOQToolsIdsMap.IMPORT_EXCEL],
    },
    {
      id: BOQToolsIdsMap.MODIFY_TOTAL,
      name: t("Modify Total"),
      disabled: !canEdit || toolbarButtonDisableState[BOQToolsIdsMap.MODIFY_TOTAL],
    },
    {
      id: BOQToolsIdsMap.EXPORT_EXCEL,
      name: t("Export Excel"),
      prefixIcon: "ExcelExportIcon",
      disabled: toolbarButtonDisableState[BOQToolsIdsMap.EXPORT_EXCEL],
    },
    {
      id: BOQToolsIdsMap.EXPORT_PDF,
      name: t("Export PDF"),
      prefixIcon: "PdfExport",
      disabled: toolbarButtonDisableState[BOQToolsIdsMap.EXPORT_PDF],
    },
    {
      id: BOQToolsIdsMap.PROPERTIES,
      rightAllign: true,
      template: propertiesBtnTemplate,
      disabled: toolbarButtonDisableState[BOQToolsIdsMap.PROPERTIES],
    },
  ];

  const toolsBySource: Partial<Record<(typeof itemListSources)[keyof typeof itemListSources], string[]>> = {
    [itemListSources.ESTIMATE]: [
      ...(!disabled
        ? [
            BOQToolsIdsMap.ADD_TOP,
            BOQToolsIdsMap.ADD_CHILD,
            BOQToolsIdsMap.EDIT,
            BOQToolsIdsMap.DELETE,
            BOQToolsIdsMap.UPDATE,
            BOQToolsIdsMap.CANCEL,
            BOQToolsIdsMap.CHANGE_TYPE,
          ]
        : []),
      BOQToolsIdsMap.EXPAND_ALL,
      BOQToolsIdsMap.COLLAPSE_ALL,
      ...(!disabled ? [BOQToolsIdsMap.IMPORT_EXCEL, BOQToolsIdsMap.MODIFY_TOTAL] : []),
      ...(disableExport ? [] : [BOQToolsIdsMap.EXPORT_EXCEL, BOQToolsIdsMap.EXPORT_PDF]),
    ],
    [itemListSources.COST_DATABASE]: [
      ...(!disabled
        ? [
            BOQToolsIdsMap.ADD_TOP,
            BOQToolsIdsMap.ADD_CHILD,
            BOQToolsIdsMap.EDIT,
            BOQToolsIdsMap.DELETE,
            BOQToolsIdsMap.UPDATE,
            BOQToolsIdsMap.CANCEL,
            BOQToolsIdsMap.IMPORT_EXCEL,
          ]
        : []),
      BOQToolsIdsMap.EXPAND_ALL,
      BOQToolsIdsMap.COLLAPSE_ALL,
      BOQToolsIdsMap.EXPORT_EXCEL,
      BOQToolsIdsMap.PROPERTIES,
    ],
    [itemListSources.ADD_ITEM_POPUP]: [BOQToolsIdsMap.EXPAND_ALL, BOQToolsIdsMap.COLLAPSE_ALL],
    [itemListSources.ESTIMATE_ATTACHED_CD_PREVIEW]: [BOQToolsIdsMap.EXPAND_ALL, BOQToolsIdsMap.COLLAPSE_ALL],
  };
  if (source == itemListSources.LINK_BOQ_ITEMS || formLinking) {
    toolsBySource[itemListSources.LINK_BOQ_ITEMS] = [
      BOQToolsIdsMap.TOGGLE,
      BOQToolsIdsMap.EXPAND_ALL,
      BOQToolsIdsMap.COLLAPSE_ALL,
    ];
  }
  if (source == itemListSources.PREVIEW_BOQ_ITEMS) {
    toolsBySource[itemListSources.PREVIEW_BOQ_ITEMS] = [BOQToolsIdsMap.EXPAND_ALL, BOQToolsIdsMap.COLLAPSE_ALL];
  }

  return allTools.filter((tool) => toolsBySource[source] && toolsBySource[source].includes(tool.id));
};

export const getRemoteDataServices = ({
  source,
  estimateId,
  estimateVersionId,
  queryProp,
  costDatabaseId,
  costDatabaseVersionId,
  publishedVersion,
  wbsOriginalItemId,
  contractVersionId,
  contractId,
  taskLinking,
  formLinking,
  formId,
  taskId,
  parentContractId,
  contractType,
}: GetRemoteDataServicesProps): GetRemoteDataServicesOutput => {
  let remoteDataSource: RemoteDataSourceType;
  let query: RemoteDataQuery | undefined;
  if (source === itemListSources.ESTIMATE || source === itemListSources.BOQ_PRINT) {
    remoteDataSource = estimateItemsUrl;
    query = {
      estimateId,
      versionId: estimateVersionId,
      params: {
        estimateId,
        versionId: estimateVersionId,
      },
    };
  } else if (source === itemListSources.LINK_BOQ_ITEMS) {
    remoteDataSource = estimateItemsForContractUrl;
    if (taskLinking || formLinking) {
      query = {
        estimateId,
        versionId: estimateVersionId,
        contractId,
        contractVersionId,
        formId,
        taskId,
      };
    } else {
      query = {
        estimateId,
        versionId: estimateVersionId,
        contractId,
        parentContractId,
        contractVersionId,
        wbsOriginalItemId: wbsOriginalItemId || undefined,
        contractType,
      };
    }
  } else if (
    source === itemListSources.COST_DATABASE ||
    source === itemListSources.ESTIMATE_ATTACHED_CD_PREVIEW ||
    source === itemListSources.CONTRACT_CDB
  ) {
    remoteDataSource = getCostDatabaseItemsUrl;
    if (source === itemListSources.COST_DATABASE || source === itemListSources.ESTIMATE_ATTACHED_CD_PREVIEW) {
      query = {
        costDatabaseId,
        versionId: costDatabaseVersionId,
        publishedVersion,
      };
    } else {
      query = {
        costDatabaseId,
        versionId: costDatabaseVersionId,
        wbsOriginalItemId,
        contractVersionId,
        contractId,
        getAllItems: true,
        params: {
          costDatabaseId,
          versionId: costDatabaseVersionId,
          wbsOriginalItemId,
          contractVersionId,
          contractId,
          getAllItems: true,
        },
      };
    }
  } else {
    remoteDataSource = getValidItemsUrl;
    query = queryProp;
  }

  return [remoteDataSource, query];
};

export const getStaticDataSource = async ({
  source,
  parentId,
  estimateId,
  estimateVersionId,
  costDatabaseId,
  costDatabaseVersionId,
  publishedVersion,
  getAllItems,
  queryProp,
}: getStaticDataSourceArgs): Promise<unknown[]> => {
  let results: unknown[] = [];
  let getItemsParams: GetEstimateAndCostDatabaseItemsArgs | undefined;
  if ((source === itemListSources.ESTIMATE || source === itemListSources.BOQ_PRINT) && estimateId) {
    getItemsParams = {
      source,
      parentId,
      estimateId,
      versionId: estimateVersionId,
      getAllItems,
    };
  } else if (
    (source === itemListSources.COST_DATABASE || source === itemListSources.ESTIMATE_ATTACHED_CD_PREVIEW) &&
    costDatabaseId
  ) {
    getItemsParams = {
      source,
      parentId,
      costDatabaseId,
      versionId: costDatabaseVersionId,
      getAllItems,
      publishedVersion,
    };
  }
  if (getItemsParams) {
    const response = await getEstimateItemsList(getItemsParams);
    results = response?.data?.result ?? [];
  }

  if (source == itemListSources.ADD_ITEM_POPUP && queryProp) {
    const response = await getEstimateValidItems({
      value: queryProp,
    });
    results = response?.data?.result ?? [];
  }

  return results;
};

export const getGridColumns1 = ({
  source,
  showHeaderWithIcon,
  showInfoColumn,
  costDatabaseCurrencySymbol,
  currencyData,
  detailTemplate,
  serialTemplate,
  totalQuantityTemplate,
  remainingQuantityTemplate,
  usedQuantityTemplate,
  quantityTemplate,
  unitTemplate,
  rateTemplate,
  amountTemplate,
  remainingAmountTemplate,
  textEditTemplate,
  quantityEditTemplate,
  unitEditTemplate,
  rateEditTemplate,
  linkedAmountTemplate,
}: GetGridColumnsProps) => {
  const { t } = i18n;
  const columnsKeyMap = {
    INFO_COLUMN: "infoColumn",
    SERIAL: "serial",
    CODE: "code",
    HSN_SAC: "HSN_SAC",
    DESCRIPTION: "description",
    ORIGINAL_QUANTITY: "originalQuantity",
    AVAILABLE_QUANTITY: "availableQuantity",
    TOTAL_QUANTITY: "totalQuantity",
    TOTAL_USED_QUANTITY: "totalUsedQuantity",
    QUANTITY: "quantity",
    PURCHASE_UNIT: "purchaseUnit",
    RATE: "rate",
    LINKED_AMOUNT: "linkedAmount",
    ORIGINAL_AMOUNT: "originalAmount",
    REMAINING_AMOUNT: "remainingAmount",
    AMOUNT: "amount",
    PARENT: "parent",
  };

  const codeHeaderRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Code")} />);
  const serialHeaderRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("S. No.")} />);
  const hsn_sacHeaderRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("HSN/SAC")} />);
  const descriptionHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Description")} />);
  const quantityHeaderRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Quantity")} />);
  const purchaseUnitHeaderRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Dropdown" label={t("Unit")} />);
  const rateHeaderRenderer = () =>
    renderToStaticMarkup(
      <HeaderWithIcon
        icon="Textbox"
        label={
          source === itemListSources.COST_DATABASE
            ? `${t("Rate")} (${costDatabaseCurrencySymbol})`
            : `${t("Rate")} (${currencyData.selectedSymbol.name})`
        }
      />,
    );

  // code
  // serialNumber
  // rate

  const colums: BOQColumn[] = [
    ...(showInfoColumn
      ? [
          {
            id: columnsKeyMap.INFO_COLUMN,
            field: "sourceFormNo",
            name: t("Form No (info)"),
            // originalWidth: 120,
            width: 120,
            disableGrouping: true,
            disableFiltering: true,
            disableSorting: true,
            formatter: detailTemplate,
          },
        ]
      : []),
    {
      id: columnsKeyMap.SERIAL,
      field: "serial",
      name: t("S. No."),
      width: source === itemListSources.BOQ_PRINT ? 50 : 60,
      filterType: "Number",
      formatter: serialTemplate,
      headerRenderer: showHeaderWithIcon ? serialHeaderRenderer : undefined,
    },
    {
      id: columnsKeyMap.CODE,
      field: "code",
      name: t("Code"),
      // filterType: source === itemListSources.BOQ_PRINT ? null : "Text",
      filterType: "Text",
      width: source === itemListSources.BOQ_PRINT ? 50 : 70,
      headerRenderer: showHeaderWithIcon ? codeHeaderRenderer : undefined,
      editorTemplate: source === itemListSources.COST_DATABASE ? () => textEditTemplate("code") : undefined,
    },
    {
      id: columnsKeyMap.HSN_SAC,
      field: "HSN_SAC",
      name: t("HSN/SAC"),
      filterType: "Text",
      // filterType: source === itemListSources.BOQ_PRINT ? null : "Text",
      initiallyHidden: true,
      width: source === itemListSources.BOQ_PRINT ? 50 : undefined,
      clipMode: "EllipsisWithTooltip",
      headerRenderer: showHeaderWithIcon ? hsn_sacHeaderRenderer : undefined,
      editorTemplate: () => textEditTemplate("HSN_SAC"),
      _excelValueGetter: (row: EstimateItemDocument) => row.HSN_SAC || "",
    },
    {
      id: columnsKeyMap.DESCRIPTION,
      field: "description",
      name: t("Description"),
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
      // excludeFromExport: true,
      // visible: false,
      // excludeFromColumnPicker: true,
      // filterType: source === itemListSources.BOQ_PRINT ? null : "Text",
      width: source == itemListSources.BOQ_PRINT ? 150 : itemListSources.ESTIMATE_ATTACHED_CD_PREVIEW ? 150 : 150,
      wrapText: true,
      headerRenderer: showHeaderWithIcon ? descriptionHeaderRenderer : undefined,
      editorTemplate: () => textEditTemplate("description"),
    },
    {
      id: columnsKeyMap.ORIGINAL_QUANTITY,
      field: "originalQuantity",
      name: t("Total Quantity"),
      disableGrouping: true,
      disableFiltering: true,
      disableSorting: true,
      // minWidth: 170,
      formatter: totalQuantityTemplate,
    },
    {
      id: columnsKeyMap.TOTAL_QUANTITY,
      field: "totalQuantity",
      name: t("Total Quantity"),
      disableGrouping: true,
      disableFiltering: true,
      disableSorting: true,

      formatter: totalQuantityTemplate,
    },
    {
      id: columnsKeyMap.AVAILABLE_QUANTITY,
      field: "availableQuantity",
      name: t("Available Quantity"),
      disableGrouping: true,
      disableFiltering: true,
      disableSorting: true,

      formatter: remainingQuantityTemplate,
    },
    {
      id: columnsKeyMap.TOTAL_USED_QUANTITY,
      field: "totalUsedQuantity",
      name: t("Total Quantity Used"),
      disableGrouping: true,
      disableFiltering: true,
      disableSorting: true,

      formatter: usedQuantityTemplate,
    },
    {
      id: columnsKeyMap.QUANTITY,
      field: "quantity",
      name: t("Quantity"),
      disableGrouping: true,
      disableFiltering: true,
      disableSorting: true,
      formatter: quantityTemplate,
      headerRenderer: showHeaderWithIcon ? quantityHeaderRenderer : undefined,
      editorTemplate: quantityEditTemplate,
    },
    {
      id: columnsKeyMap.PURCHASE_UNIT,
      field: "purchaseUnit.name",
      name: t("Unit"),

      // minWidth: source == itemListSources.BOQ_PRINT ? 100 : 150,

      filterType: "Text",
      formatter: unitTemplate,
      headerRenderer: showHeaderWithIcon ? purchaseUnitHeaderRenderer : undefined,
      editorTemplate: unitEditTemplate,
      _excelValueGetter: (row: EstimateItemDocument) => row.purchaseUnit?.name || "",
    },
    {
      id: columnsKeyMap.RATE,
      field: "rate",
      name:
        source === itemListSources.COST_DATABASE
          ? `${t("Rate")} (${costDatabaseCurrencySymbol})`
          : `${t("Rate")} (${currencyData.selectedSymbol.name})`,

      disableGrouping: true,
      disableFiltering: true,
      disableSorting: true,
      width: source === itemListSources.BOQ_PRINT ? 50 : undefined,
      formatter: rateTemplate,
      headerRenderer: showHeaderWithIcon ? rateHeaderRenderer : undefined,
      editorTemplate: rateEditTemplate,
      _excelValueGetter: (row: EstimateItemDocument) => row.rate || "",
    },
    {
      id: columnsKeyMap.LINKED_AMOUNT,
      field: "linkedAmount",
      name: `${t("Amount used in this form")} (${currencyData.selectedSymbol.name})`,
      disableGrouping: true,
      disableFiltering: true,
      disableSorting: true,
      formatter: linkedAmountTemplate,
    },
    {
      id: columnsKeyMap.ORIGINAL_AMOUNT,
      field: "originalAmount",
      name: `${t("Total Amount")} (${currencyData.selectedSymbol.name})`,
      disableGrouping: true,
      disableFiltering: true,
      disableSorting: true,

      // minWidth: 170,
      formatter: amountTemplate,
    },
    {
      id: columnsKeyMap.REMAINING_AMOUNT,
      field: "remainingAmount",
      name: `${t("Remaining Amount")} (${currencyData.selectedSymbol.name})`,
      disableGrouping: true,
      disableFiltering: true,
      disableSorting: true,

      // minWidth: 170,
      formatter: remainingAmountTemplate,
    },
    {
      id: columnsKeyMap.AMOUNT,
      field: "amount",
      name: `${t("Amount")} (${currencyData.selectedSymbol.name})`,
      disableGrouping: true,
      disableFiltering: true,
      disableSorting: true,
      // minWidth: source == itemListSources.BOQ_PRINT ? 120 : 150,
      formatter: amountTemplate,
    },
    {
      id: columnsKeyMap.PARENT,
      field: "parent",
      name: t("Parent"),
      disableGrouping: true,
      disableFiltering: true,
      disableSorting: true,
      hidden: true,
    },
  ];

  const defaultCDColumns = [
    columnsKeyMap.CODE,
    columnsKeyMap.HSN_SAC,
    columnsKeyMap.DESCRIPTION,
    columnsKeyMap.PURCHASE_UNIT,
    columnsKeyMap.RATE,
  ];

  const columnsBySourceMap: Partial<Record<(typeof itemListSources)[keyof typeof itemListSources], string[]>> = {
    [itemListSources.ESTIMATE]: [
      columnsKeyMap.SERIAL,
      columnsKeyMap.CODE,
      columnsKeyMap.HSN_SAC,
      columnsKeyMap.DESCRIPTION,
      columnsKeyMap.QUANTITY,
      columnsKeyMap.PURCHASE_UNIT,
      columnsKeyMap.RATE,
      columnsKeyMap.AMOUNT,
    ],
    [itemListSources.BOQ_PRINT]: [
      columnsKeyMap.SERIAL,
      columnsKeyMap.CODE,
      columnsKeyMap.HSN_SAC,
      columnsKeyMap.DESCRIPTION,
      columnsKeyMap.QUANTITY,
      columnsKeyMap.PURCHASE_UNIT,
      columnsKeyMap.RATE,
      columnsKeyMap.AMOUNT,
    ],
    [itemListSources.PREVIEW_BOQ_ITEMS]: [
      columnsKeyMap.INFO_COLUMN,
      columnsKeyMap.SERIAL,
      columnsKeyMap.CODE,
      columnsKeyMap.HSN_SAC,
      columnsKeyMap.DESCRIPTION,
      columnsKeyMap.TOTAL_QUANTITY,
      columnsKeyMap.AVAILABLE_QUANTITY,
      columnsKeyMap.TOTAL_USED_QUANTITY,
      columnsKeyMap.QUANTITY,
      columnsKeyMap.PURCHASE_UNIT,
      columnsKeyMap.RATE,
      columnsKeyMap.LINKED_AMOUNT,
      columnsKeyMap.ORIGINAL_AMOUNT,
      columnsKeyMap.REMAINING_AMOUNT,
    ],
    [itemListSources.LINK_BOQ_ITEMS]: [
      columnsKeyMap.INFO_COLUMN,
      columnsKeyMap.SERIAL,
      columnsKeyMap.CODE,
      columnsKeyMap.HSN_SAC,
      columnsKeyMap.DESCRIPTION,
      columnsKeyMap.ORIGINAL_QUANTITY,
      columnsKeyMap.AVAILABLE_QUANTITY,
      columnsKeyMap.QUANTITY,
      columnsKeyMap.PURCHASE_UNIT,
      columnsKeyMap.RATE,
      columnsKeyMap.ORIGINAL_AMOUNT,
      columnsKeyMap.REMAINING_AMOUNT,
    ],
    [itemListSources.COST_DATABASE]: [...defaultCDColumns, columnsKeyMap.PARENT],
    [itemListSources.ESTIMATE_ATTACHED_CD_PREVIEW]: defaultCDColumns,
    [itemListSources.ADD_ITEM_POPUP]: defaultCDColumns,
    [itemListSources.CONTRACT_CDB]: defaultCDColumns,
  };

  const sourceColumns = colums.filter((column) => columnsBySourceMap[source]?.includes(column.id as string));
  sourceColumns.map((column) => {
    if (source === itemListSources.ESTIMATE && column.id === columnsKeyMap.SERIAL) {
      column.formatter = Formatters.tree;
    } else if (
      (source === itemListSources.COST_DATABASE ||
        source === itemListSources.ADD_ITEM_POPUP ||
        source === itemListSources.ESTIMATE_ATTACHED_CD_PREVIEW ||
        source === itemListSources.CONTRACT_CDB) &&
      column.id === columnsKeyMap.CODE
    ) {
      column.formatter = Formatters.tree;
    } else if (
      (source === itemListSources.LINK_BOQ_ITEMS || source === itemListSources.PREVIEW_BOQ_ITEMS) &&
      column.id === columnsKeyMap.SERIAL
    ) {
      column.formatter = Formatters.tree;
    }
  });
  return sourceColumns;
};
